// lib/notebook-context.tsx
"use client";

import * as React from "react";
import {
  deleteStudyNotebooksById,
  deleteStudyNotebooksByIdDocumentsByDocId,
  deleteStudyNotebooksByIdHighlightsByHighlightId,
  getStudyNotebooksByIdHighlightsByHighlightId,
  getStudyNotebooksByNotebookIdDocumentsByNotebookDocumentIdFile,
  Notebook,
  postStudyFileNotebooks,
  postStudyNotebooks,
  putStudyNotebooksById,
  putStudyNotebooksByIdDocumentsByDocId,
} from "../client";
import {
  FinalAssignment,
  NotebookExtended,
  StudyDocument,
  StudyNotUploadedDocument,
} from "../types";
import { useCommunication } from "@/lib/CommunicationWrapper";
import { toast } from "sonner";
import {
  HighlightCategory,
  HighlightMaterials,
} from "../components/layout/highlights/highlights";
import { CanvasCourse } from "@/lib/types";
import { useAuth } from "@/lib/auth";

// =============================================================================
// Types
// =============================================================================

export type CreateFlowStep =
  | "landing"
  | "course"
  | "files_init"
  | "files"
  | "done";

export type CurrentPage =
  | { type: "source"; value: StudyDocument }
  | { type: "chat" }
  | { type: "highlight_category"; value: HighlightCategory }
  | { type: "highlight"; value: HighlightMaterials }
  | {
      type: "source_highlight";
      source: StudyDocument;
      highlight: HighlightMaterials;
    }
  | {
      type: "source_highlight_category";
      source: StudyDocument;
      highlight_category: HighlightCategory;
    };

type NotebookState = {
  notebooks: Record<number, NotebookExtended>;
  selectedNotebookId: number | null;
  selectedSourceIds: Set<number>;
  courseFiles: StudyNotUploadedDocument[];
  loadingSources: boolean;
  createFlowStep: CreateFlowStep;
  currentPage: CurrentPage;
  sourceUrlCache: Record<number, string>;
};

type NotebookAction =
  | { type: "SET_NOTEBOOKS"; notebooks: NotebookExtended[] }
  | { type: "SET_SELECTED_NOTEBOOK"; id: number | null }
  | { type: "ADD_NOTEBOOK"; notebook: NotebookExtended }
  | { type: "UPDATE_NOTEBOOK"; id: number; notebook: Partial<NotebookExtended> }
  | { type: "DELETE_NOTEBOOK"; id: number }
  | { type: "ADD_SOURCE"; notebookId: number; source: StudyDocument }
  | { type: "UPDATE_SOURCE"; notebookId: number; source: StudyDocument }
  | { type: "REMOVE_SOURCE"; notebookId: number; sourceId: number }
  | { type: "ADD_HIGHLIGHT"; notebookId: number; highlight: HighlightMaterials }
  | {
      type: "UPDATE_HIGHLIGHT";
      notebookId: number;
      highlight: HighlightMaterials;
    }
  | { type: "REMOVE_HIGHLIGHT"; notebookId: number; highlightId: number }
  | { type: "SET_SELECTED_SOURCES"; sourceIds: number[] }
  | { type: "ADD_SELECTED_SOURCE"; sourceId: number }
  | { type: "REMOVE_SELECTED_SOURCE"; sourceId: number }
  | { type: "CLEAR_SELECTED_SOURCES" }
  | { type: "SET_COURSE_FILES"; files: StudyNotUploadedDocument[] }
  | { type: "SET_LOADING_SOURCES"; loading: boolean }
  | { type: "SET_CREATE_FLOW_STEP"; step: CreateFlowStep }
  | { type: "SET_CURRENT_PAGE"; page: CurrentPage }
  | { type: "CACHE_SOURCE_URL"; sourceId: number; url: string };

export type NotebookContextValue = {
  notebook: NotebookExtended | null;
  notebooks: NotebookExtended[];
  sources: StudyDocument[];
  selectedSources: StudyDocument[];
  courseFiles: StudyNotUploadedDocument[];
  loadingSources: boolean;
  createFlowStep: CreateFlowStep;
  highlights: HighlightMaterials[];
  currentPage: CurrentPage;

  setNotebook: (notebook: Notebook) => Promise<void>;
  setSelectedSources: (sources: StudyDocument[]) => void;
  addSelectedSource: (source: StudyDocument) => void;
  removeSelectedSource: (source: StudyDocument) => void;
  clearSelectedSources: () => void;
  addSource: (source: StudyDocument) => Promise<void>;
  removeSource: (source: StudyDocument) => Promise<void>;
  editSource: (source: StudyDocument) => Promise<void>;
  editNotebook: (notebook: Notebook) => Promise<void>;
  deleteNotebook: (notebook: Notebook) => Promise<void>;
  createNotebook: (data: {
    name: string;
    course: CanvasCourse;
    exam?: FinalAssignment;
    fileId?: string;
  }) => Promise<NotebookExtended>;
  addHighlight: (highlight: HighlightMaterials) => void;
  removeHighlight: (highlight: HighlightMaterials) => Promise<void>;
  getHighlightContents: (highlight: HighlightMaterials) => Promise<any>;
  getSourceUrl: (document: StudyDocument) => Promise<string>;
  setCreateFlowStep: (step: CreateFlowStep) => void;
  navigatePage: (page: CurrentPage) => void;
};

// =============================================================================
// Reducer
// =============================================================================

function notebookReducer(
  state: NotebookState,
  action: NotebookAction
): NotebookState {
  switch (action.type) {
    case "SET_NOTEBOOKS": {
      const notebooks = Object.fromEntries(
        action.notebooks.map((nb) => [nb.id, nb])
      );
      return { ...state, notebooks };
    }

    case "SET_SELECTED_NOTEBOOK": {
      if (action.id === null) {
        return {
          ...state,
          selectedNotebookId: null,
          selectedSourceIds: new Set(),
          currentPage: { type: "chat" },
        };
      }

      const notebook = state.notebooks[action.id];
      if (!notebook) return state;

      return {
        ...state,
        selectedNotebookId: action.id,
        selectedSourceIds: new Set(
          notebook.notebook_documents.map((d) => d.id)
        ),
        currentPage: { type: "chat" },
      };
    }

    case "ADD_NOTEBOOK": {
      return {
        ...state,
        notebooks: {
          ...state.notebooks,
          [action.notebook.id]: action.notebook,
        },
        selectedNotebookId: action.notebook.id,
        selectedSourceIds: new Set(
          action.notebook.notebook_documents.map((nd) => nd.id)
        ),
        createFlowStep: "files_init",
        currentPage: { type: "chat" },
      };
    }

    case "UPDATE_NOTEBOOK": {
      const existing = state.notebooks[action.id];
      if (!existing) return state;

      return {
        ...state,
        notebooks: {
          ...state.notebooks,
          [action.id]: { ...existing, ...action.notebook },
        },
      };
    }

    case "DELETE_NOTEBOOK": {
      const { [action.id]: deleted, ...remainingNotebooks } = state.notebooks;
      const notebookIds = Object.keys(remainingNotebooks).map(Number);

      let newSelectedId = state.selectedNotebookId;
      let newFlowStep = state.createFlowStep;

      if (state.selectedNotebookId === action.id) {
        if (notebookIds.length === 0) {
          newSelectedId = null;
          newFlowStep = "landing";
        } else {
          newSelectedId = notebookIds[0];
        }
      }

      return {
        ...state,
        notebooks: remainingNotebooks,
        selectedNotebookId: newSelectedId,
        createFlowStep: newFlowStep,
        ...(newSelectedId === null ? { selectedSourceIds: new Set() } : {}),
      };
    }

    case "ADD_SOURCE": {
      const notebook = state.notebooks[action.notebookId];
      if (!notebook) return state;

      const newNotebookDocuments = notebook.notebook_documents.find(
        (x) => x.document_id === action.source.document_id
      )
        ? [...notebook.notebook_documents]
        : [action.source, ...notebook.notebook_documents];

      return {
        ...state,
        notebooks: {
          ...state.notebooks,
          [action.notebookId]: {
            ...notebook,
            notebook_documents: newNotebookDocuments,
          },
        },
        selectedSourceIds: new Set([
          ...state.selectedSourceIds,
          action.source.id,
        ]),
      };
    }

    case "UPDATE_SOURCE": {
      const notebook = state.notebooks[action.notebookId];
      if (!notebook) return state;

      const updatedDocs = notebook.notebook_documents.map((doc) =>
        doc.id === action.source.id ? action.source : doc
      );

      let newCurrentPage = state.currentPage;
      if (
        state.currentPage.type === "source" &&
        state.currentPage.value.id === action.source.id
      ) {
        newCurrentPage = { type: "source", value: action.source };
      }

      return {
        ...state,
        notebooks: {
          ...state.notebooks,
          [action.notebookId]: {
            ...notebook,
            notebook_documents: updatedDocs,
          },
        },
        currentPage: newCurrentPage,
      };
    }

    case "REMOVE_SOURCE": {
      const notebook = state.notebooks[action.notebookId];
      if (!notebook) return state;

      const filteredDocs = notebook.notebook_documents.filter(
        (doc) => doc.id !== action.sourceId
      );

      const newSelectedSourceIds = new Set(state.selectedSourceIds);
      newSelectedSourceIds.delete(action.sourceId);

      let newCurrentPage = state.currentPage;
      if (
        state.currentPage.type === "source" &&
        state.currentPage.value.id === action.sourceId
      ) {
        newCurrentPage = { type: "chat" };
      }

      return {
        ...state,
        notebooks: {
          ...state.notebooks,
          [action.notebookId]: {
            ...notebook,
            notebook_documents: filteredDocs,
          },
        },
        selectedSourceIds: newSelectedSourceIds,
        currentPage: newCurrentPage,
      };
    }

    case "ADD_HIGHLIGHT": {
      const notebook = state.notebooks[action.notebookId];
      if (!notebook) return state;

      // Avoid duplicates
      const existingHighlight = notebook.highlights.find(
        (h) => h.id === action.highlight.id
      );
      if (existingHighlight) return state;

      return {
        ...state,
        notebooks: {
          ...state.notebooks,
          [action.notebookId]: {
            ...notebook,
            highlights: [...notebook.highlights, action.highlight],
          },
        },
      };
    }

    case "UPDATE_HIGHLIGHT": {
      const notebook = state.notebooks[action.notebookId];
      if (!notebook) return state;

      const updatedHighlights = notebook.highlights.map((h) =>
        h.id === action.highlight.id ? action.highlight : h
      );

      let newCurrentPage = state.currentPage;
      if (
        state.currentPage.type === "highlight" &&
        state.currentPage.value.id === action.highlight.id
      ) {
        newCurrentPage = { type: "highlight", value: action.highlight };
      }

      return {
        ...state,
        notebooks: {
          ...state.notebooks,
          [action.notebookId]: {
            ...notebook,
            highlights: updatedHighlights,
          },
        },
        currentPage: newCurrentPage,
      };
    }

    case "REMOVE_HIGHLIGHT": {
      const notebook = state.notebooks[action.notebookId];
      if (!notebook) return state;

      return {
        ...state,
        notebooks: {
          ...state.notebooks,
          [action.notebookId]: {
            ...notebook,
            highlights: notebook.highlights.filter(
              (h) => h.id !== action.highlightId
            ),
          },
        },
      };
    }

    case "SET_SELECTED_SOURCES": {
      return {
        ...state,
        selectedSourceIds: new Set(action.sourceIds),
      };
    }

    case "ADD_SELECTED_SOURCE": {
      if (state.selectedSourceIds.has(action.sourceId)) return state;
      return {
        ...state,
        selectedSourceIds: new Set([
          ...state.selectedSourceIds,
          action.sourceId,
        ]),
      };
    }

    case "REMOVE_SELECTED_SOURCE": {
      return {
        ...state,
        selectedSourceIds: new Set(
          [...state.selectedSourceIds].filter((s) => s !== action.sourceId)
        ),
      };
    }

    case "CLEAR_SELECTED_SOURCES": {
      return {
        ...state,
        selectedSourceIds: new Set(),
      };
    }

    case "SET_COURSE_FILES": {
      return {
        ...state,
        courseFiles: action.files,
      };
    }

    case "SET_LOADING_SOURCES": {
      return {
        ...state,
        loadingSources: action.loading,
      };
    }

    case "SET_CREATE_FLOW_STEP": {
      return {
        ...state,
        createFlowStep: action.step,
      };
    }

    case "SET_CURRENT_PAGE": {
      if (state.currentPage.type === "source") {
        if (action.page.type === "highlight")
          return {
            ...state,
            currentPage: {
              type: "source_highlight",
              source: state.currentPage.value,
              highlight: action.page.value,
            },
          };
        else if (action.page.type === "highlight_category")
          return {
            ...state,
            currentPage: {
              type: "source_highlight_category",
              source: state.currentPage.value,
              highlight_category: action.page.value,
            },
          };
      } else if (state.currentPage.type === "source_highlight") {
        if (action.page.type === "highlight")
          return {
            ...state,
            currentPage: {
              type: "source_highlight",
              source: state.currentPage.source,
              highlight: action.page.value,
            },
          };
        else if (action.page.type === "highlight_category")
          return {
            ...state,
            currentPage: {
              type: "source_highlight_category",
              source: state.currentPage.source,
              highlight_category: action.page.value,
            },
          };
      } else if (state.currentPage.type === "source_highlight_category") {
        if (action.page.type === "highlight")
          return {
            ...state,
            currentPage: {
              type: "source_highlight",
              source: state.currentPage.source,
              highlight: action.page.value,
            },
          };
        else if (action.page.type === "highlight_category")
          return {
            ...state,
            currentPage: {
              type: "source_highlight_category",
              source: state.currentPage.source,
              highlight_category: action.page.value,
            },
          };
      }
      return {
        ...state,
        currentPage: action.page,
      };
    }

    case "CACHE_SOURCE_URL": {
      return {
        ...state,
        sourceUrlCache: {
          ...state.sourceUrlCache,
          [action.sourceId]: action.url,
        },
      };
    }

    default:
      return state;
  }
}

// =============================================================================
// Context
// =============================================================================

const NotebookContext = React.createContext<NotebookContextValue | null>(null);

export function NotebookProvider({
  defaultNotebook,
  allNotebooks,
  children,
}: {
  defaultNotebook?: NotebookExtended;
  allNotebooks: NotebookExtended[];
  children: React.ReactNode;
}) {
  const initialState: NotebookState = {
    notebooks: Object.fromEntries(allNotebooks.map((nb) => [nb.id, nb])),
    selectedNotebookId: defaultNotebook?.id ?? null,
    selectedSourceIds: new Set(
      defaultNotebook?.notebook_documents.map((d) => d.id) ?? []
    ),
    courseFiles: [],
    loadingSources: false,
    createFlowStep: "landing",
    currentPage: { type: "chat" },
    sourceUrlCache: {},
  };

  console.log("INTIIAL", initialState);

  const [state, dispatch] = React.useReducer(notebookReducer, initialState);
  const { sendMessageAndListen } = useCommunication();
  const { authUser } = useAuth();

  // =============================================================================
  // Derived State
  // =============================================================================

  const notebook = state.selectedNotebookId
    ? state.notebooks[state.selectedNotebookId] ?? null
    : null;

  const notebooks = React.useMemo(
    () => Object.values(state.notebooks).sort((a, b) => b.id - a.id),
    [state.notebooks]
  );

  const sources = notebook?.notebook_documents ?? [];

  const selectedSources = React.useMemo(
    () => sources.filter((s) => state.selectedSourceIds.has(s.id)),
    [sources, state.selectedSourceIds]
  );

  const highlights = notebook?.highlights ?? [];

  // =============================================================================
  // Effects
  // =============================================================================

  // Load course files when notebook changes
  React.useEffect(() => {
    if (!notebook) return;

    (async () => {
      dispatch({ type: "SET_LOADING_SOURCES", loading: true });

      const result = await sendMessageAndListen({
        type: "CALL_EXTENSION_FUNC",
        payload: {
          func: "fetchCourseFiles",
          args: { course_id: notebook.course_id.toString() },
        },
      });

      if (result.success && result.data) {
        dispatch({
          type: "SET_COURSE_FILES",
          files: result.data as StudyNotUploadedDocument[],
        });
      }

      dispatch({ type: "SET_LOADING_SOURCES", loading: false });
    })();
  }, [notebook?.id]);

  // =============================================================================
  // Actions
  // =============================================================================

  const setNotebook = React.useCallback(
    async (notebook: Notebook) => {
      dispatch({ type: "SET_SELECTED_NOTEBOOK", id: notebook.id });

      const selectedNotebook = state.notebooks[notebook.id];
      if (selectedNotebook) {
        const step =
          selectedNotebook.notebook_documents.length === 0
            ? "files_init"
            : "done";
        dispatch({ type: "SET_CREATE_FLOW_STEP", step });
      }
    },
    [state.notebooks]
  );

  const setSelectedSources = React.useCallback((sources: StudyDocument[]) => {
    dispatch({
      type: "SET_SELECTED_SOURCES",
      sourceIds: sources.map((s) => s.id),
    });
  }, []);

  const addSelectedSource = React.useCallback((source: StudyDocument) => {
    dispatch({ type: "ADD_SELECTED_SOURCE", sourceId: source.id });
  }, []);

  const removeSelectedSource = React.useCallback((source: StudyDocument) => {
    dispatch({ type: "REMOVE_SELECTED_SOURCE", sourceId: source.id });
  }, []);

  const clearSelectedSources = React.useCallback(() => {
    dispatch({ type: "CLEAR_SELECTED_SOURCES" });
  }, []);

  const addSource = React.useCallback(
    async (source: StudyDocument) => {
      if (!notebook) return;
      dispatch({
        type: "ADD_SOURCE",
        notebookId: source.notebook_id || notebook.id,
        source,
      });
    },
    [notebook]
  );

  const removeSource = React.useCallback(
    async (source: StudyDocument) => {
      if (!notebook) return;

      try {
        dispatch({
          type: "REMOVE_SOURCE",
          notebookId: notebook.id,
          sourceId: source.id,
        });

        await deleteStudyNotebooksByIdDocumentsByDocId({
          path: {
            id: source.notebook_id.toString(),
            doc_id: source.id.toString(),
          },
        });
      } catch (e) {
        console.error(e);
        toast.error("Failed to remove source from study set.", {
          description: (
            <span className="text-theme-1">{JSON.stringify(e)}</span>
          ),
        });
      }
    },
    [notebook]
  );

  const editSource = React.useCallback(
    async (updated: StudyDocument) => {
      if (!notebook) return;

      try {
        await putStudyNotebooksByIdDocumentsByDocId({
          path: {
            id: updated.notebook_id.toString(),
            doc_id: updated.id.toString(),
          },
          body: {
            name: updated.name,
          },
        });

        dispatch({
          type: "UPDATE_SOURCE",
          notebookId: notebook.id,
          source: updated,
        });
      } catch (e) {
        console.error(e);
        toast.error("Failed to edit source.", {
          description: (
            <span className="text-theme-1">{JSON.stringify(e)}</span>
          ),
        });
      }
    },
    [notebook]
  );

  const editNotebook = React.useCallback(async (editedNotebook: Notebook) => {
    if (!editedNotebook.id) return;

    try {
      const { data, error } = await putStudyNotebooksById({
        path: { id: editedNotebook.id.toString() },
        body: {
          name: editedNotebook.name,
          assignment_id: editedNotebook.assignment_id,
          ...(editedNotebook.metadata
            ? { metadata: editedNotebook.metadata }
            : {}),
        },
      });

      if (error) throw error;

      dispatch({
        type: "UPDATE_NOTEBOOK",
        id: editedNotebook.id,
        notebook: data,
      });
    } catch (e) {
      console.error(e);
      toast.error("Failed to rename notebook.", {
        description: <span className="text-theme-1">{JSON.stringify(e)}</span>,
      });
    }
  }, []);

  const deleteNotebook = React.useCallback(
    async (deletedNotebook: Notebook) => {
      if (!deletedNotebook.id) return;

      try {
        const { error } = await deleteStudyNotebooksById({
          path: { id: deletedNotebook.id.toString() },
        });

        if (error) throw error;

        dispatch({ type: "DELETE_NOTEBOOK", id: deletedNotebook.id });
      } catch (e) {
        console.error(e);
        toast.error("Failed to delete notebook.", {
          description: (
            <span className="text-theme-1">{JSON.stringify(e)}</span>
          ),
        });
      }
    },
    []
  );

  const createNotebook = React.useCallback(
    async (data: {
      name: string;
      course: CanvasCourse;
      exam?: FinalAssignment;
      fileId?: string;
    }) => {
      const { data: domain } = await sendMessageAndListen({
        type: "CALL_EXTENSION_FUNC",
        payload: { func: "getDomain" },
      });

      const { data: created, error } = !data.fileId
        ? await postStudyNotebooks({
            body: {
              user_id: authUser.id,
              course_id: data.course.id.toString(),
              ...(data.exam ? { assignment_id: data.exam.id } : {}),
              name: data.name,
              school_url: domain,
              metadata: {
                school_url: domain,
                assignment: data.exam || {},
                course: data.course || {},
              },
            },
          })
        : await postStudyFileNotebooks({
            body: {
              user_id: authUser.id,
              course_id: data.course.id.toString(),
              ...(data.exam ? { assignment_id: data.exam.id } : {}),
              name: data.name,
              school_url: domain,
              metadata: {
                school_url: domain,
                assignment: data.exam || {},
                course: data.course || {},
              },
              type: data.fileId,
            },
          });

      if (error) {
        toast.error("Error creating notebook", {
          description: (
            <span className="text-theme-1">{JSON.stringify(error)}</span>
          ),
        });
        throw error;
      }

      const extendedNotebook: NotebookExtended = {
        ...created,
        notebook_documents:
          "notebook_documents" in created
            ? (created as NotebookExtended).notebook_documents
            : [],
        highlights:
          "highlights" in created
            ? (created as NotebookExtended).highlights
            : [],
      };

      dispatch({ type: "ADD_NOTEBOOK", notebook: extendedNotebook });
      return extendedNotebook;
    },
    [authUser, sendMessageAndListen]
  );

  const addHighlight = React.useCallback(
    (highlight: HighlightMaterials) => {
      if (!notebook) return;
      dispatch({
        type: "ADD_HIGHLIGHT",
        notebookId: notebook.id,
        highlight,
      });
    },
    [notebook]
  );

  const removeHighlight = React.useCallback(
    async (removed: HighlightMaterials) => {
      if (!notebook) return;

      try {
        await deleteStudyNotebooksByIdHighlightsByHighlightId({
          path: {
            id: notebook.id.toString(),
            highlight_id: removed.id.toString(),
          },
        });

        dispatch({
          type: "REMOVE_HIGHLIGHT",
          notebookId: notebook.id,
          highlightId: removed.id,
        });
      } catch (e) {
        console.error(e);
        toast.error("Failed to delete material.", {
          description: (
            <span className="text-theme-1">{JSON.stringify(e)}</span>
          ),
        });
      }
    },
    [notebook]
  );

  const getHighlightContents = React.useCallback(
    async (highlight: HighlightMaterials) => {
      if (!notebook) return undefined;
      if (highlight.contents) return highlight.contents;

      try {
        const { data, error } =
          await getStudyNotebooksByIdHighlightsByHighlightId({
            path: {
              id: highlight.notebook_id.toString(),
              highlight_id: highlight.id.toString(),
            },
          });

        if (error) throw error;

        dispatch({
          type: "UPDATE_HIGHLIGHT",
          notebookId: notebook.id,
          highlight: data,
        });

        return data.contents;
      } catch (e) {
        console.error(e);
        toast.error(`Failed to retrieve ${highlight.type} contents.`, {
          description: (
            <span className="text-theme-1">{JSON.stringify(e)}</span>
          ),
        });
        return undefined;
      }
    },
    [notebook]
  );

  const getSourceUrl = React.useCallback(
    async (document: StudyDocument): Promise<string> => {
      if (state.sourceUrlCache[document.id]) {
        return state.sourceUrlCache[document.id];
      }

      const res =
        await getStudyNotebooksByNotebookIdDocumentsByNotebookDocumentIdFile({
          path: {
            notebook_id: document.notebook_id.toString(),
            notebook_document_id: document.id.toString(),
          },
        });

      dispatch({
        type: "CACHE_SOURCE_URL",
        sourceId: document.id,
        url: res.data.url,
      });

      return res.data.url;
    },
    [state.sourceUrlCache]
  );

  const setCreateFlowStep = React.useCallback((step: CreateFlowStep) => {
    dispatch({ type: "SET_CREATE_FLOW_STEP", step });
  }, []);

  const navigatePage = React.useCallback((page: CurrentPage) => {
    dispatch({ type: "SET_CURRENT_PAGE", page });
  }, []);

  // =============================================================================
  // Context Value
  // =============================================================================

  const value = React.useMemo<NotebookContextValue>(
    () => ({
      notebook,
      notebooks,
      sources,
      selectedSources,
      courseFiles: state.courseFiles,
      loadingSources:
        state.loadingSources ||
        (notebook &&
          notebook.course_id &&
          !!state.courseFiles.find(
            (f) => f.metadata["canvas_course_id"] !== notebook.course_id
          )),
      createFlowStep: state.createFlowStep,
      highlights,
      currentPage: state.currentPage,
      setNotebook,
      setSelectedSources,
      addSelectedSource,
      removeSelectedSource,
      clearSelectedSources,
      addSource,
      removeSource,
      editSource,
      editNotebook,
      deleteNotebook,
      createNotebook,
      addHighlight,
      removeHighlight,
      getHighlightContents,
      getSourceUrl,
      setCreateFlowStep,
      navigatePage,
    }),
    [
      notebook,
      notebooks,
      sources,
      selectedSources,
      state.courseFiles,
      state.loadingSources,
      state.createFlowStep,
      highlights,
      state.currentPage,
      setNotebook,
      setSelectedSources,
      addSelectedSource,
      removeSelectedSource,
      clearSelectedSources,
      addSource,
      removeSource,
      editSource,
      editNotebook,
      deleteNotebook,
      createNotebook,
      addHighlight,
      removeHighlight,
      getHighlightContents,
      getSourceUrl,
      setCreateFlowStep,
      navigatePage,
    ]
  );

  return (
    <NotebookContext.Provider value={value}>
      {children}
    </NotebookContext.Provider>
  );
}

export function useNotebookContext() {
  const ctx = React.useContext(NotebookContext);
  if (!ctx) {
    throw new Error(
      "useNotebookContext must be used within a NotebookProvider"
    );
  }
  return ctx;
}
